import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { FiTrash2, FiRotateCw, FiEdit3 } from 'react-icons/fi';
import Table from '../shared/table/Table';
import { Link } from 'react-router-dom';
import useTranslations from '../../hooks/useTranslations';
import useUserLanguage from '../../hooks/useUserLanguage';
import { confirmAlert } from "react-confirm-alert"; // başta import
import { toast } from "react-toastify";

const TrashRoleTable = () => {
  const [roles, setRoles] = useState([]);
  const [perms, setPerms] = useState([]);

  const lang = useUserLanguage();
  const { translations } = useTranslations(lang);
  const t = (key, def) => translations[key] || def;

  // Verileri getir
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const [permRes, trashRes] = await Promise.all([
          axios.get('${process.env.REACT_APP_API_BASE_URL}/api/admin/my-permissions', {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get('${process.env.REACT_APP_API_BASE_URL}/api/roles/list/trash', {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        setPerms(permRes.data.permissions || []);
        setRoles(trashRes.data || []);
      } catch (err) {
        console.error('Çöp kutusu yüklenirken hata:', err);
      }
    })();
  }, []);

  // Yetki kontrolü
  const modulePerm = perms.find(p => p.module_key === 'static_roles') || {};
  const canEdit    = modulePerm.edit_permission   || modulePerm.full_access;
  const canRestore = modulePerm.edit_permission   || modulePerm.full_access;
  const canDestroy = modulePerm.delete_permission || modulePerm.full_access;

  const handleRestore = (id) => {
    confirmAlert({
      title: t("RESTORE_CONFIRM_TITLE", "Rolü Geri Yükle"),
      message: t("RESTORE_CONFIRM_MSG", "Bu rolü geri yüklemek istediğinize emin misiniz?"),
      buttons: [
        {
          label: t("YES_RESTORE", "Evet, Yükle"),
          onClick: async () => {
            try {
              const token = localStorage.getItem("admin_token");
              await axios.put(
                `${process.env.REACT_APP_API_BASE_URL}/api/roles/${id}/restore`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
              );
              setRoles(prev => prev.filter(i => i.id !== id));
              toast.success(t("SUCCESS_ROLE_RESTORED", "Rol başarıyla geri yüklendi!"));
            } catch (err) {
              console.error(err);
              toast.error(t("ERROR_ROLE_RESTORE_FAILED", "Rol geri yüklenemedi."));
            }
          }
        },
        {
          label: t("CANCEL", "İptal"),
          onClick: () => toast.info(t("RESTORE_CANCELLED", "İşlem iptal edildi.")),
        }
      ],
    });
  };

  const handleDestroy = (id) => {
    confirmAlert({
      title: t("DESTROY_CONFIRM_TITLE", "Rolü Kalıcı Olarak Sil"),
      message: t("DESTROY_CONFIRM_MSG", "Bu rolü kalıcı olarak silmek istediğinize emin misiniz?"),
      buttons: [
        {
          label: t("YES_DESTROY", "Evet, Sil"),
          onClick: async () => {
            try {
              const token = localStorage.getItem("admin_token");
              await axios.delete(
                `${process.env.REACT_APP_API_BASE_URL}/api/roles/${id}/permanent`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              setRoles(prev => prev.filter(i => i.id !== id));
              toast.success(t("SUCCESS_ROLE_DESTROYED", "Rol kalıcı olarak silindi!"));
            } catch (err) {
              console.error(err);
              toast.error(t("ERROR_ROLE_DESTROY_FAILED", "Rol kalıcı olarak silinemedi."));
            }
          }
        },
        {
          label: t("CANCEL", "İptal"),
          onClick: () => toast.info(t("DESTROY_CANCELLED", "İşlem iptal edildi.")),
        }
      ],
    });
  };
  

  const columns = useMemo(() => [
    { accessorKey: 'id', header: t("TABLE_ID_LABEL", "ID"), size: 60 },
    { accessorKey: 'role_name', header: t("TABLE_TITLE_LABEL", "Rol Adı"), size: 200 },
    {
      accessorKey: 'created_at',
      header: t("TABLE_CREATED_AT", "Oluşturulma Tarihi"),
      meta: { headerClassName: 'text-end', className: 'text-end date-column' },
      cell: (info) => {
        const rawDate = info.getValue();
        const formattedDate = new Date(rawDate).toLocaleDateString('en-GB');
        return (
          <div style={{ textAlign: 'end', width: '125px' }}>
            {formattedDate}
          </div>
        );
      },
      size: 160
    },
    {
      accessorKey: 'deleted_at',
      header: t("TABLE_DELETED_AT", "Silinme Tarihi"),
      meta: { headerClassName: 'text-end', className: 'text-end date-column' },
      cell: (info) => {
        const rawDate = info.getValue();
        const formattedDate = new Date(rawDate).toLocaleDateString('en-GB');
        return (
          <div style={{ textAlign: 'end', width: '125px' }}>
            {formattedDate}
          </div>
        );
      },
      size: 160
    },
    {
      accessorKey: 'actions',
      header: t("TABLE_ACTIONS_LABEL", "İşlemler"),
      enableSorting: false,
      size: 140,
      cell: info => {
        const id = info.row.original.id;
        return (
          <div className="d-flex gap-2 justify-content-end">
            {canEdit && (
              <Link
                to={`/admin/roles/edit/${id}`}
                className="avatar-text avatar-md text-success bg-soft-success border-soft-success"
                title={t("EDIT", "Düzenle")}
              >
                <FiEdit3 />
              </Link>
            )}
            {canRestore && (
              <button
                className="avatar-text avatar-md text-primary bg-soft-primary border-soft-primary"
                title={t("RESTORE", "Geri Yükle")}
                onClick={() => handleRestore(id)}
              ><FiRotateCw /></button>
            )}
            {canDestroy && (
              <button
                className="avatar-text avatar-md text-danger bg-soft-danger border-soft-danger"
                title={t("DELETE_PERMANENT", "Kalıcı Sil")}
                onClick={() => handleDestroy(id)}
              ><FiTrash2 /></button>
            )}
          </div>
        );
      }
    }
  ], [t, canEdit, canRestore, canDestroy, roles]);

  return <Table data={roles} columns={columns} />;
};

export default TrashRoleTable;
